import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Primitive } from '@amzn/swo-unified-dashboard-service-lambda-js-client/lib/smswounifieddashboardservicelambda';
import { WebsiteViewService } from '../../model/WebsiteViewService';
import { PrimitiveWorkflowStatus } from '../website-view/constants';

/** Whether anything in the website's most recent processing cycle is still moving. */
export type WebsiteRunStatus = 'RUNNING' | 'DONE';

/**
 * Deliberately the key `useTimelineData` already uses. Both want the same response for the same domain, so
 * keyed identically react-query serves the badge and the timeline from one request rather than asking for the
 * website's events twice on a page that renders both.
 */
export const WEBSITE_EVENTS_QUERY_KEY = 'timeline';

const startedAt = (primitive: Primitive): number => Number(primitive.startTime ?? 0);

/**
 * Still running, read the way the timeline already reads it, so a badge saying DONE cannot sit above a
 * pipeline drawn mid-flight: either no usable end time, or a state that says so outright. `endTime` is unset
 * until an END event lands, which is the ordinary case for a primitive that is still going rather than a
 * malformed one.
 */
const isStillRunning = (primitive: Primitive): boolean => {
  const state = primitive.metadata?.['state'];
  if (
    typeof state === 'string' &&
    state.toLowerCase() === PrimitiveWorkflowStatus.RUNNING.toLowerCase()
  ) {
    return true;
  }
  const endTime = Number(primitive.endTime);
  return !Number.isFinite(endTime) || endTime <= 0;
};

/**
 * RUNNING when any primitive of the newest trace has not finished, DONE when all of them have.
 *
 * The newest trace only, because a website is processed in cycles and each trace is one cycle: an older cycle
 * that was abandoned half way through would otherwise pin the badge to RUNNING for good. The newest is found
 * by start time rather than by the order the search returned, which is not guaranteed to be either.
 *
 * Null, not DONE, when there are no primitives at all — DONE would assert that a cycle finished for a website
 * nothing has indexed yet. Null renders no badge, which is also what a failed or in-flight request gives.
 *
 * DONE says nothing is still moving; it does not say the cycle succeeded. A trace whose every primitive
 * failed and stopped reads DONE too, which is why the badge's tooltip states it rather than leaving the word
 * to imply a verdict the primitives cannot support.
 */
export const websiteRunStatus = (primitives: Primitive[] | undefined): WebsiteRunStatus | null => {
  const traced = (primitives ?? []).filter((primitive) => !!primitive.traceId);
  if (traced.length === 0) {
    return null;
  }
  const newest = traced.reduce((latest, candidate) =>
    startedAt(candidate) > startedAt(latest) ? candidate : latest
  );
  const latestCycle = traced.filter((primitive) => primitive.traceId === newest.traceId);
  return latestCycle.some(isStillRunning) ? 'RUNNING' : 'DONE';
};

/**
 * The website's latest cycle, from the primitives the website view already fetches — the same
 * `getWebsiteEvents` call behind the timeline, rather than a second endpoint answering the same question.
 *
 * The options match `useTimelineData`'s exactly. They have to: two observers of one key that disagreed about
 * staleness would turn the shared cache entry into a refetch loop between them.
 */
export const useWebsiteRunStatus = (domain: string | undefined) => {
  const { data, isLoading, error } = useQuery({
    queryKey: [WEBSITE_EVENTS_QUERY_KEY, domain],
    queryFn: () => new WebsiteViewService().getWebsiteEvents(domain || ''),
    enabled: !!domain,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    status: useMemo(() => websiteRunStatus(data?.primitives), [data]),
    isLoading,
    error,
  };
};
